import { LoadingOutlined } from "@ant-design/icons";
import { Spin } from "antd";
import Konva from "konva";
import { memo, useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { AsyncStates } from "src/constants";
import { StoreState } from "src/store/configureStore";
import useImage from "use-image";
import { addZoomAndPan } from "../../Shared/canvasZoomPan";
import { extractNumericValue } from "../../Shared/MaskTable";
import { useParams } from "react-router-dom";

type TProps = {
    showAllMasks: boolean;
    filteredData: any;
    image: any;
    showBBox: boolean;
    showSkeleton: boolean;
    setHoveredMaskDetails: React.Dispatch<React.SetStateAction<Record<string, any>>>,
    showGraphAnalysis: boolean,
    showJunctionPoints: boolean,
    showEndPointPoints: boolean,
    scaleHeightOverlay: number,
    setScaleHeightOverlay: React.Dispatch<React.SetStateAction<number>>
};

const SCALE_STEPS = 1.1

const FiberAnalysisDetailsContent = ({
    showAllMasks,
    filteredData,
    image,
    showBBox,
    showSkeleton,
    setHoveredMaskDetails,
    showGraphAnalysis,
    showJunctionPoints,
    showEndPointPoints,
    scaleHeightOverlay,
}: TProps) => {
    const [img] = useImage(image);
    const { semAnalysisMaskDataStatus, modifyFiberStatus, phaseTwoFiberAnalysisData } = useSelector(
        (state: StoreState) => state.semAnalysis
    );
    const [mounted, setMounted] = useState(false);
    const params = useParams<any>();

    const stage = useMemo(() => {
        if (!mounted) return;

        // Setup
        const canvasWidth = img?.width || 1000;
        const canvasHeight = img?.height || 1000;

        const stage = new Konva.Stage({
            container: "image-masks", // id of container <div>
            width: canvasWidth,
            height: canvasHeight,
        });

        // Add zoom
        addZoomAndPan(stage, SCALE_STEPS);

        return stage;
    }, [img?.height, img?.width, mounted]);

    useEffect(() => {
        if (!mounted) setMounted(true);

        // Make sure data & stage is not empty
        if (!filteredData || !Array.isArray(filteredData) || !stage) return;

        const layer = new Konva.Layer();
        stage.add(layer);
        layer.draw();

        const skeletonBBoxLayer = new Konva.Layer();
        stage.add(skeletonBBoxLayer);

        const layerForCirclesApi = new Konva.Layer();
        stage.add(layerForCirclesApi);
        layerForCirclesApi.draw();

        const layerForCircles = new Konva.Layer();
        stage.add(layerForCircles);
        layerForCircles.draw();

        // Adding Image
        const img_layer = new Konva.Image({
            image: img,
        });

        layer.add(img_layer);
        layer.batchDraw();

        let dataArr: any = filteredData;

        dataArr?.forEach((mask: any) => {
            const pol = new Konva.Line({
                points: mask?.contour_points?.flat() ?? [],
                stroke: "green",
                fill: "transparent",
                strokeWidth: 0,
                closed: true,
                id: mask.id,
                visible: true,
                listening: true
            });

            if (showSkeleton) {
                // recheck this
                const junctionPointData = mask?.junction_points

                mask?.skeleton_points?.forEach((point: any, mask_index: number) => {
                    const skeleton = new Konva.Circle({
                        radius: 1,
                        fill: "#e0a604",
                        x: point[0],
                        y: point[1],
                        listening: true,
                    });


                    if (showJunctionPoints && Array.isArray(junctionPointData) && junctionPointData?.length > 0) {
                        skeleton.on('mouseenter', () => {
                            const group = junctionPointData?.[mask_index];
                            if (Array.isArray(group) && group.length > 0) {
                                const xs = group.map((p: number[]) => p[0]);
                                const ys = group.map((p: number[]) => p[1]);
                                const minX = Math.min(...xs);
                                const minY = Math.min(...ys);
                                const maxX = Math.max(...xs);
                                const maxY = Math.max(...ys);
                                const width = maxX - minX;
                                const height = maxY - minY;

                                const bbox = new Konva.Rect({
                                    x: minX,
                                    y: minY,
                                    width: width,
                                    height: height,
                                    stroke: 'blue',
                                    strokeWidth: 1,
                                    dash: [4, 2],
                                    name: 'skeleton-bbox'
                                });
                                skeletonBBoxLayer.add(bbox);
                                skeletonBBoxLayer.draw();
                            }
                        });

                        skeleton.on('mouseleave', () => {
                            skeletonBBoxLayer.find('.skeleton-bbox').forEach(node => node.destroy());
                            skeletonBBoxLayer.draw();
                        });
                    }
                    layer.add(skeleton);

                });
            }

            const boundBoxData = pol?.getClientRect();

            const bbox = new Konva.Rect({
                x: boundBoxData.x,
                y: boundBoxData.y,
                width: boundBoxData.width,
                height: boundBoxData.height,
                fill: "transparent",
                stroke: "red",
                strokeWidth: 0,
                id: mask.id,
                visible: true,
            });

            if (showAllMasks) pol.fill(mask?.mask_color);
            if (showBBox) bbox.strokeWidth(2);



            bbox.on("mouseenter", () => {
                bbox.strokeWidth(2);
                stage.container().style.cursor = "pointer";
                const unit = "px";
                setHoveredMaskDetails({
                    "Width": `${boundBoxData.width} ${unit}`,
                    "Length": `${boundBoxData.height} ${unit}`,
                    "Mask ID": extractNumericValue(mask.id)
                })
            });

            bbox.on("mouseleave", () => {
                if (!showBBox) {
                    bbox.strokeWidth(0);
                }
                stage.container().style.cursor = "default";
                setHoveredMaskDetails({})
            });

            layer.add(pol);
            layer.add(bbox);
            pol.moveToTop();

            // recheck this
            const endPointDistanceData = mask?.endpoints;
            if (
                showEndPointPoints &&
                Array.isArray(endPointDistanceData) &&
                endPointDistanceData.length
            ) {
                endPointDistanceData.forEach((point) => {
                    if (Array.isArray(point) && point.length === 2) {
                        const [x, y] = point;
                        const pointCircle = new Konva.Circle({
                            x,
                            y,
                            radius: 4,
                            fill: 'orange',
                            stroke: 'black',
                            strokeWidth: 1,
                        });
                        layer.add(pointCircle);
                    }
                });
            }

        });

        if (!showGraphAnalysis) {
            const scaleValue = scaleHeightOverlay / 100
            const bottomOverlay = new Konva.Rect({
                x: 0,
                y: stage.height() * (1 - scaleValue),
                width: stage.width(),
                height: stage.height() * scaleValue,
                fill: "#000000",
                listening: false,
            });

            layer.add(bottomOverlay);
            bottomOverlay.moveToTop();
        }

        layer.draw();


        return () => {
            layer.removeChildren();
            layer.destroy();
        }
    }, [filteredData, img, mounted, setHoveredMaskDetails, showGraphAnalysis, scaleHeightOverlay, showAllMasks, showBBox, showEndPointPoints, showJunctionPoints, showSkeleton, stage]);

    return (
        <div className="image-mask-container">
            <Spin
                indicator={<LoadingOutlined style={{ color: "white" }} />}
                spinning={[semAnalysisMaskDataStatus, modifyFiberStatus].includes(AsyncStates.LOADING) || phaseTwoFiberAnalysisData?.[params?.fileId]?.status === "processing"}
                style={{ height: "100%", width: "100%", marginTop: "50px" }}
            >
                <div
                    id="image-masks"
                    style={{
                        display: "flex",
                        justifyContent: "center",
                        alignItems: "center",
                        overflow: "auto",
                    }}
                />
            </Spin>
        </div>
    );
};

export default memo(FiberAnalysisDetailsContent);
